import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  alpha,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Badge,
  Tooltip,
  Zoom,
  Fade,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  CarRental as CarRentalIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  Dashboard as DashboardIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Verified as VerifiedIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoImage from '../../assets/logo.png';

interface HeaderProps {
  onLogin: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogin }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Menu items with icons and descriptions
  const menuItems = [
    { 
      label: 'Home', 
      icon: <HomeIcon />, 
      path: '/',
      description: 'Return to homepage',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    { 
      label: 'About Us', 
      icon: <InventoryIcon />, 
      path: '/about',
      description: 'Track vehicle stock and specifications',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
    },
    { 
      label: 'Contact', 
      icon: <ContactIcon />, 
      path: '/contact',
      description: 'Get in touch with our team',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    },
  ];

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
    setActiveSection(sectionId);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Premium mobile menu drawer
  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: 320,
          background: 'linear-gradient(135deg, #0B1120 0%, #1A1F35 100%)',
          color: 'white',
          borderTopLeftRadius: 24,
          borderBottomLeftRadius: 24,
        },
      }}
      SlideProps={{ direction: 'left' }}
    >
      <Box sx={{ p: 3 }}>
        {/* Close Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton 
            onClick={() => setMobileMenuOpen(false)}
            sx={{ 
              color: 'white',
              background: alpha('#fff', 0.1),
              '&:hover': { background: alpha('#fff', 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Logo in drawer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
          <Avatar src={LogoImage} sx={{ width: 48, height: 48 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'white' }}>
              Sadaka
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
             Enterprise 1
            </Typography>
          </Box>
        </Box>

        {/* Menu Items */}
        <List sx={{ mb: 3 }}>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  background: location.pathname === item.path ? alpha('#667eea', 0.2) : 'transparent',
                  border: '1px solid',
                  borderColor: location.pathname === item.path ? alpha('#667eea', 0.5) : 'transparent',
                  '&:hover': {
                    background: alpha('#fff', 0.1),
                  },
                }}
              >
                <ListItemIcon sx={{ color: alpha('#fff', 0.8), minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography sx={{ fontWeight: 600, color: 'white' }}>
                      {item.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                      {item.description}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: alpha('#fff', 0.1), my: 2 }} />

        {/* Social Links */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), display: 'block', mb: 2 }}>
            Connect With Us
          </Typography>
          <Stack direction="row" spacing={1}>
            {[
              { icon: <FacebookIcon />, color: '#1877F2' },
              { icon: <TwitterIcon />, color: '#1DA1F2' },
              { icon: <LinkedInIcon />, color: '#0A66C2' },
              { icon: <InstagramIcon />, color: '#E4405F' },
            ].map((social, index) => (
              <IconButton
                key={index}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  background: alpha(social.color, 0.1),
                  color: social.color,
                  '&:hover': {
                    background: social.color,
                    color: 'white',
                  },
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Box>

        {/* Login Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            onLogin();
            setMobileMenuOpen(false);
          }}
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontWeight: 700,
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}
        >
          Access Dashboard
        </Button>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: scrolled 
            ? alpha(theme.palette.background.paper, 0.98)
            : alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          boxShadow: scrolled 
            ? '0 4px 30px rgba(0,0,0,0.1)' 
            : '0 2px 20px rgba(0,0,0,0.05)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ py: { xs: 1, md: scrolled ? 1 : 2 }, transition: 'all 0.3s ease' }}>
            {/* Premium Logo with Animation */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                  '& .logo-icon': {
                    transform: 'rotate(10deg) scale(1.1)',
                  },
                  '& .logo-text': {
                    backgroundPosition: 'right center',
                  },
                },
              }}
              onClick={() => navigate('/')}
            >
              <Box className="logo-icon" sx={{ width: 45, height: 45, borderRadius: '14px', overflow: 'hidden', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)' }}>
                <img src={LogoImage} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
              <Box>
                <Typography
                  className="logo-text"
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.3rem',
                    lineHeight: 1.2,
                    background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                    backgroundSize: '200% auto',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    transition: 'background-position 0.5s ease',
                  }}
                >
                  Sadaka
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: 1,
                    display: 'block',
                  }}
                >
                  ENTERPRISE 1
                </Typography>
              </Box>

              {/* Verified Badge */}
              <Tooltip title="Enterprise Solution" arrow placement="right">
                <Box
                  sx={{
                    ml: 1,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#4CAF50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VerifiedIcon sx={{ fontSize: 14, color: 'white' }} />
                </Box>
              </Tooltip>
            </Box>

            {/* Desktop Menu - Premium */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                {menuItems.map((item) => (
                  <Box
                    key={item.label}
                    onMouseEnter={() => setHoveredItem(item.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{ position: 'relative' }}
                  >
                    <Button
                      onClick={() => handleNavigation(item.path)}
                      startIcon={item.icon}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 3,
                        color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: hoveredItem === item.label || location.pathname === item.path ? '80%' : '0%',
                          height: '3px',
                          background: item.gradient,
                          borderRadius: '3px 3px 0 0',
                          transition: 'width 0.3s ease',
                        },
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {item.label}
                    </Button>

                    {/* Tooltip on hover */}
                    {hoveredItem === item.label && (
                      <Fade in timeout={200}>
                        <Paper
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            mt: 1,
                            p: 1.5,
                            minWidth: 200,
                            background: alpha(theme.palette.background.paper, 0.95),
                            backdropFilter: 'blur(10px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                            borderRadius: 2,
                            zIndex: 9999,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {item.description}
                          </Typography>
                        </Paper>
                      </Fade>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Right Section - Premium */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

              {/* Login Button - Premium */}
              {!isMobile ? (
                <Button
                  variant="contained"
                  onClick={onLogin}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 3,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'left 0.5s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 30px rgba(102, 126, 234, 0.4)',
                      '&::before': {
                        left: '100%',
                      },
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Access Dashboard
                </Button>
              ) : (
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    width: 45,
                    height: 45,
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer to prevent content hiding under fixed appbar */}
      <Toolbar sx={{ py: { xs: 1, md: 2 }, opacity: 0 }} />

      {mobileMenu}
    </>
  );
};

export default Header;